// ClimatologyDialog.js
import { useState, useEffect } from "react"
import { Typography, Button } from '@mui/material'
import ClimatologySnowMeltForm from './ClimatologySnowMeltForm'
import ClimatologyTemperatureForm from './ClimatologyTemperatureForm'
import ClimatologyADCForm from './ClimatologyADCForm'
import ClimatologyEvaporationForm from './ClimatologyEvaporationForm'
import ClimatologyAdjustmentsForm from './ClimatologyAdjustmentsForm'
import ClimatologyWindSpeedForm from './ClimatologyWindSpeedForm'
import "../../css/DemoCode.css"

/**
 * Frame for data inputs. 
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @returns UI for interactive swmm modeling.
 */
export default function ClimatologyDialog({model, setModel}) {
  const [submodel, setSubmodel] = useState({})

  useEffect(()=>{
    let TEMPERATURE = JSON.parse(JSON.stringify(model.TEMPERATURE))
    let EVAPORATION = JSON.parse(JSON.stringify(model.EVAPORATION))
    let ADJUSTMENTS = JSON.parse(JSON.stringify(model.ADJUSTMENTS))

    // Create a group of base values
    if(!TEMPERATURE.SNOWMELT){
      TEMPERATURE.SNOWMELT={
        DivideTemp: "34",
        ATIWeight: "0.5",
        NegMeltRatio: "0.6",
        MSLElev: "0",
        DegLatitude: "50",
        LongCorrection: "0"
      }
    }// Create a group of base values
    if(!TEMPERATURE.ADC){
      TEMPERATURE.ADC={
        PERVIOUS: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
        IMPERVIOUS: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
      }
    }// Create a group of base values
    if(!TEMPERATURE.WINDSPEED){
      TEMPERATURE.WINDSPEED={
        Type: 'MONTHLY',
        AWS: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      }
    }// Create a group of base values
    if(!ADJUSTMENTS.MONTHLY){
      ADJUSTMENTS.MONTHLY = {}
    }
    if(!ADJUSTMENTS.MONTHLY.TEMPERATURE){
      ADJUSTMENTS.MONTHLY.TEMPERATURE = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }// Create a group of base values
    if(!ADJUSTMENTS.MONTHLY.EVAPORATION){
      ADJUSTMENTS.MONTHLY.EVAPORATION = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }// Create a group of base values
    if(!ADJUSTMENTS.MONTHLY.RAINFALL){
      ADJUSTMENTS.MONTHLY.RAINFALL = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
    }// Create a group of base values
    if(!ADJUSTMENTS.MONTHLY.CONDUCTIVITY){
      ADJUSTMENTS.MONTHLY.CONDUCTIVITY = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
    }

    let SNOWMELT  = JSON.parse(JSON.stringify(model.TEMPERATURE.SNOWMELT ?model.TEMPERATURE.SNOWMELT:{}))
    let ADC       = JSON.parse(JSON.stringify(model.TEMPERATURE.ADC      ?model.TEMPERATURE.ADC:{}))
    let WINDSPEED = JSON.parse(JSON.stringify(model.TEMPERATURE.WINDSPEED?model.TEMPERATURE.WINDSPEED:{}))

    setSubmodel({
      TEMPERATURE: {
        SNOWMELT: SNOWMELT,
        ADC: ADC,
        WINDSPEED: WINDSPEED,
        ...TEMPERATURE
      },
      ADJUSTMENTS: {
        MONTHLY:{
          TEMPERATURE:  ADJUSTMENTS.MONTHLY.TEMPERATURE,
          EVAPORATION:  ADJUSTMENTS.MONTHLY.EVAPORATION,
          RAINFALL:     ADJUSTMENTS.MONTHLY.RAINFALL,
          CONDUCTIVITY: ADJUSTMENTS.MONTHLY.CONDUCTIVITY,
        },
        ...ADJUSTMENTS,
      },
      EVAPORATION: EVAPORATION
    })
  },[model])

  const saveEdits = () => {
    let cleanSub = JSON.parse(JSON.stringify(submodel))
    
    setModel(prev => ({
      ...prev,
      TEMPERATURE: cleanSub.TEMPERATURE,
      EVAPORATION: cleanSub.EVAPORATION,
      ADJUSTMENTS: cleanSub.ADJUSTMENTS
    }))
  }

  function openTab(evt, tabName) {
    var i, tabcontent, tablinks
  
    // Get all elements with class="tabcontent" and hide them
    tabcontent = document.getElementsByClassName("tabcontent")
    for (i = 0; i < tabcontent.length; i++) {
      tabcontent[i].style.display = "none"
    }
  
    // Get all elements with class="tablinks" and remove the class "active"
    tablinks = document.getElementsByClassName("tablinks")
    for (i = 0; i < tablinks.length; i++) {
      tablinks[i].className = tablinks[i].className.replace(" active", "")
    }
  
    // Show the current tab, and add an "active" class to the Button that opened the tab
    document.getElementById(tabName).style.display = "block"
    evt.currentTarget.className += " active"
  }

  /**
   * If there is a valid model, create a UI for the model.
   */
  return(
    <>
    {
      <>
        <Typography id="modal-modal-title" variant="p4" >
          <div style={{display:"flex", flexDirection: "column"}}>
            <label>Climatology</label>
          </div>
        </Typography>
        <div>
          <Button fullWidth onClick={saveEdits} >Save Edits</Button>
        </div>
        <div style={{background:'black', overflowY: 'hidden'}}>
          {
            <>
            <div className="tab">
              <Button className="tablinks active" onClick={(e)=>openTab(e, 'Temperature'    )}>Temperature</Button>
              <Button className="tablinks" onClick={(e)=>openTab(e, 'Evaporation'    )}>Evaporation</Button>
              <Button className="tablinks" onClick={(e)=>openTab(e, 'Wind Speed'     )}>Wind Speed</Button>
              <Button className="tablinks" onClick={(e)=>openTab(e, 'Snow Melt'      )}>Snow Melt</Button>
              <Button className="tablinks" onClick={(e)=>openTab(e, 'Areal Depletion')}>Areal Depletion</Button>
              <Button className="tablinks" onClick={(e)=>openTab(e, 'Adjustments'    )}>Adjustments</Button>
            </div>
            
            <div id="Temperature" style={{display: 'block'}} className="tabcontent">
              {<ClimatologyTemperatureForm model={submodel} setModel={setSubmodel} fullModel={model} />}
            </div>

            <div id="Evaporation" className="tabcontent">
              {<ClimatologyEvaporationForm model={submodel} setModel={setSubmodel} fullModel={model} />}
            </div>
            
            <div id="Wind Speed" className="tabcontent">
              {<ClimatologyWindSpeedForm model={submodel} setModel={setSubmodel} fullModel={model} />}
            </div>
            
            <div id="Snow Melt" className="tabcontent">
              {<ClimatologySnowMeltForm model={submodel} setModel={setSubmodel} fullModel={model} />}
            </div>
            
            <div id="Areal Depletion" className="tabcontent">
              {<ClimatologyADCForm model={submodel} setModel={setSubmodel} fullModel={model} />}
            </div>
            
            <div id="Adjustments" className="tabcontent">
              {<ClimatologyAdjustmentsForm model={submodel} setModel={setSubmodel} />}
            </div>
            
            </>
          }
        </div>
        </>
      }
    </>
  )
} 